import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Mbp4RegisterApi} from "../../../api/Mbp4RegisterApi";

// ── CONTRACT ENTRY columns (18 cols) ─────────────────────────────────────────
const CONTRACT_COLUMNS = [
  { key: "serialNo",                   label: "S.No",                               col: "(1)"  },
  { key: "dateOfContract",             label: "Date of Contract",                   col: "(2)"  },
  { key: "partyName",                  label: "Name of Party",                      col: "(3)"  },
  { key: "interestedDirectorName",     label: "Interested Director Name",           col: "(4)"  },
  { key: "relationOrNatureOfInterest", label: "Relation / Nature of Interest",      col: "(5)"  },
  { key: "principalTerms",             label: "Principal Terms",                    col: "(6)"  },
  { key: "armsLength",                 label: "Arms Length Transaction",            col: "(7)"  },
  { key: "boardApprovalDate",          label: "Board Approval Date",                col: "(8)"  },
  { key: "directorsPresent",           label: "Directors Present",                  col: "(9)"  },
  { key: "directorsInFavour",          label: "Directors in Favour",                col: "(10)" },
  { key: "directorsAgainst",           label: "Directors Against",                  col: "(11)" },
  { key: "directorsNeutral",           label: "Directors Neutral",                  col: "(12)" },
  { key: "nextMeetingDate",            label: "Next Meeting Date",                  col: "(13)" },
  { key: "section188Reference",        label: "Section 188 Reference",              col: "(14)" },
  { key: "contractAmount",             label: "Contract Amount (₹)",                col: "(15)" },
  { key: "shareholdersApprovalDate",   label: "Shareholders Approval Date",         col: "(16)" },
  { key: "remarks",                    label: "Remarks",                            col: "(17)" },
];

// CONTRACT: Part 1 cols (1)–(9), Part 2 S.No + (10)–(17)
const CONTRACT_PART_ONE = CONTRACT_COLUMNS.slice(0, 9);
const CONTRACT_PART_TWO = [CONTRACT_COLUMNS[0], ...CONTRACT_COLUMNS.slice(9)];

// ── DIRECTOR INTEREST columns ─────────────────────────────────────────────────
const INTEREST_COLUMNS = [
  { key: "serialNo",                       label: "S.No",                               col: "(1)" },
  { key: "bodyName",                        label: "Name of Body Corporate / Firm",      col: "(2)" },
  { key: "interestedDirectorName",          label: "Interested Director Name",           col: "(3)" },
  { key: "natureOfInterest",               label: "Nature of Interest",                 col: "(4)" },
  { key: "shareholding",                   label: "Shareholding (%)",                   col: "(5)" },
  { key: "dateOfInterestArisedOrChanged",  label: "Date Interest Arose / Changed",      col: "(6)" },
];

// INTEREST: all 6 cols fit in one table — no split needed
// but we keep the same pattern for consistency
const INTEREST_PART_ONE = INTEREST_COLUMNS; // single part

const BLANK_CONTRACT = {
  dateOfContract: "", partyName: "", interestedDirectorName: "",
  relationOrNatureOfInterest: "", principalTerms: "", armsLength: "",
  boardApprovalDate: "", directorsPresent: "", directorsInFavour: "",
  directorsAgainst: "", directorsNeutral: "", nextMeetingDate: "",
  section188Reference: "", contractAmount: "", shareholdersApprovalDate: "", remarks: "",
};

const BLANK_INTEREST = {
  bodyName: "", interestedDirectorName: "", natureOfInterest: "",
  shareholding: "", dateOfInterestArisedOrChanged: "",
};

const contractDateFields  = ["dateOfContract", "boardApprovalDate", "nextMeetingDate", "shareholdersApprovalDate"];
const contractNumberFields = ["directorsPresent", "directorsInFavour", "directorsAgainst", "directorsNeutral", "contractAmount"];
const interestDateFields   = ["dateOfInterestArisedOrChanged"];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
};

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return "—"; }
};

// Inject serialNo into rows for display
const withSerial = (rows) => rows.map((row, i) => ({ ...row, serialNo: i + 1 }));

// ── Screen sub-table ──────────────────────────────────────────────────────────
const SubTable = ({ columns, rows, partLabel, dateFieldSet = [] }) => (
  <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
    <div className="bg-slate-100 border-b border-slate-200 px-4 py-1.5">
      <span className="text-[11px] font-bold text-slate-600 uppercase tracking-widest">{partLabel}</span>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-[10px] border-collapse">
        <thead>
          <tr className="bg-slate-800 text-white">
            {columns.map(col => (
              <th key={col.key} className="border border-slate-600 px-2 py-1 text-center font-bold text-[9px] whitespace-nowrap">
                {col.col}
              </th>
            ))}
          </tr>
          <tr className="bg-slate-700 text-white">
            {columns.map(col => (
              <th
                key={col.key}
                className="border border-slate-600 px-2 py-2 text-center font-semibold text-[9px] leading-tight"
                style={{ minWidth: col.key === "serialNo" ? 36 : 100 }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id || i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
              {columns.map(col => (
                <td key={col.key} className="border border-slate-200 px-2 py-1.5 text-center text-[10px] align-middle">
                  {dateFieldSet.includes(col.key) ? fmtDate(row[col.key]) : fmt(row[col.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ── Add Entry Modal ───────────────────────────────────────────────────────────
const AddEntryModal = ({ onClose, onSave, saving, parentRecords }) => {
  const [selectedParentId, setSelectedParentId] = useState(
    parentRecords.length === 1 ? parentRecords[0].id : ""
  );
  const [activeTab, setActiveTab] = useState("contract"); // "contract" | "interest"
  const [contractForm, setContractForm] = useState({ ...BLANK_CONTRACT });
  const [interestForm, setInterestForm] = useState({ ...BLANK_INTEREST });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="bg-primary text-white px-6 py-3 rounded-t-xl flex justify-between items-center">
          <h2 className="font-bold text-sm uppercase tracking-widest">Add New MBP-4 Entry</h2>
          <button onClick={onClose} className="text-white hover:text-red-300 text-xl font-bold">✕</button>
        </div>

        <div className="overflow-y-auto p-4 flex-1">
          {/* Parent selector */}
          {parentRecords.length > 1 && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <label className="block text-xs font-semibold text-blue-700 mb-1">Select Record *</label>
              <select
                value={selectedParentId}
                onChange={e => setSelectedParentId(e.target.value)}
                className="w-full px-2 py-1.5 border border-blue-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="">— Select —</option>
                {parentRecords.map(p => (
                  <option key={p.id} value={p.id}>
                    {fmtDate(p.date)} | {p.place} | Signed by: {p.signedBy || "—"}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Tab switcher */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setActiveTab("contract")}
              className={`px-4 py-1.5 text-xs font-bold rounded-md uppercase transition ${
                activeTab === "contract"
                  ? "bg-slate-800 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              Contract Entry
            </button>
            <button
              onClick={() => setActiveTab("interest")}
              className={`px-4 py-1.5 text-xs font-bold rounded-md uppercase transition ${
                activeTab === "interest"
                  ? "bg-slate-800 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              Director Interest Entry
            </button>
          </div>

          {/* Contract form */}
          {activeTab === "contract" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {CONTRACT_COLUMNS.filter(c => c.key !== "serialNo").map(col => (
                <div key={col.key}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{col.col} {col.label}</label>
                  {col.key === "armsLength" ? (
                    <select
                      value={contractForm.armsLength}
                      onChange={e => setContractForm(prev => ({ ...prev, armsLength: e.target.value }))}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                      <option value="">— Select —</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  ) : (
                    <input
                      type={contractDateFields.includes(col.key) ? "date" : contractNumberFields.includes(col.key) ? "number" : "text"}
                      value={contractForm[col.key] || ""}
                      onChange={e => setContractForm(prev => ({ ...prev, [col.key]: e.target.value }))}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Interest form */}
          {activeTab === "interest" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {INTEREST_COLUMNS.filter(c => c.key !== "serialNo").map(col => (
                <div key={col.key}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{col.col} {col.label}</label>
                  <input
                    type={interestDateFields.includes(col.key) ? "date" : col.key === "shareholding" ? "number" : "text"}
                    value={interestForm[col.key] || ""}
                    onChange={e => setInterestForm(prev => ({ ...prev, [col.key]: e.target.value }))}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-xs border border-gray-300 rounded-md hover:bg-gray-50 font-semibold uppercase">
            Cancel
          </button>
          <button
            onClick={() => onSave(activeTab === "contract" ? contractForm : interestForm, selectedParentId, activeTab)}
            disabled={saving || !selectedParentId}
            className="px-4 py-2 text-xs bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 font-semibold uppercase"
          >
            {saving ? "Saving..." : `Save ${activeTab === "contract" ? "Contract" : "Interest"} Entry`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Print styles ──────────────────────────────────────────────────────────────
const PRINT_STYLES = `
  @media print {
    @page { size: A3 landscape; margin: 8mm; }
    body * { visibility: hidden !important; }
    #mbp4-pdf-area, #mbp4-pdf-area * { visibility: visible !important; }
    #mbp4-pdf-area { position: fixed; top: 0; left: 0; width: 100%; font-family: Arial, sans-serif; }
  }
`;

// ── Main MBP-4 Viewer ─────────────────────────────────────────────────────────
const Mbp4Viewer = ({ selectedClientData }) => {
  const token     = useSelector((state) => state.auth?.access_token);
  const accountId = selectedClientData?.accountId || selectedClientData?.id;

  const [parentRecords, setParentRecords] = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [showModal,     setShowModal]     = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [status,        setStatus]        = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  useEffect(() => {
    const style = document.createElement("style");
    style.id = "mbp4-print-style";
    style.innerHTML = PRINT_STYLES;
    if (!document.getElementById("mbp4-print-style")) document.head.appendChild(style);
    return () => document.getElementById("mbp4-print-style")?.remove();
  }, []);

  const fetchData = useCallback(async () => {
    if (!accountId || !token) return;
    setLoading(true);
    setError(null);
    try {
      const res  = await Mbp4RegisterApi.fetchMbp4Register(accountId, token);
      const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      setParentRecords(list);
    } catch (err) {
      setError("No data available for MBP-4");
    } finally {
      setLoading(false);
    }
  }, [accountId, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveEntry = async (form, parentId, entryType) => {
    setSaving(true);
    setStatus(null);
    try {
      let payload;
      if (entryType === "contract") {
        payload = {
          ...form,
          armsLength:                form.armsLength === "true" ? true : form.armsLength === "false" ? false : null,
          contractAmount:            form.contractAmount            ? parseFloat(form.contractAmount)            : null,
          directorsPresent:          form.directorsPresent          ? parseInt(form.directorsPresent, 10)        : null,
          directorsInFavour:         form.directorsInFavour         ? parseInt(form.directorsInFavour, 10)       : null,
          directorsAgainst:          form.directorsAgainst          ? parseInt(form.directorsAgainst, 10)        : null,
          directorsNeutral:          form.directorsNeutral          ? parseInt(form.directorsNeutral, 10)        : null,
          dateOfContract:            form.dateOfContract            || null,
          boardApprovalDate:         form.boardApprovalDate         || null,
          nextMeetingDate:           form.nextMeetingDate           || null,
          shareholdersApprovalDate:  form.shareholdersApprovalDate  || null,
        };
        await Mbp4RegisterApi.addNewContracts( parentId, payload, token);
      } else {
        payload = {
          ...form,
          shareholding:                       form.shareholding ? parseFloat(form.shareholding) : null,
          dateOfInterestArisedOrChanged:      form.dateOfInterestArisedOrChanged || null,
        };
        await Mbp4RegisterApi.addNewDirectorOfInterest( parentId, payload, token);
      }
      setShowModal(false);
      setStatus({ type: "success", message: "Entry added successfully!" });
      await fetchData();
    } catch (err) {
      setStatus({ type: "error", message: err.message || "Failed to add entry." });
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = () => {
    setGeneratingPdf(true);
    setTimeout(() => { window.print(); setGeneratingPdf(false); }, 300);
  };

  const totalContracts = parentRecords.reduce((s, p) => s + (p.contractEntries?.length || 0), 0);
  const totalInterests = parentRecords.reduce((s, p) => s + (p.directorInterestEntries?.length || 0), 0);
  const hasData        = totalContracts + totalInterests > 0;

  if (!selectedClientData) {
    return <p className="text-center text-gray-500 mt-10">Select an account to view MBP-4 data.</p>;
  }

  // ── PDF sub-table ─────────────────────────────────────────────────────────
  const PdfSubTable = ({ columns, rows, partLabel, dateFieldSet = [] }) => (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 12, fontWeight: "bold", color: "#000", marginBottom: 3, textTransform: "uppercase", letterSpacing: 0.8 }}>
        {partLabel}
      </div>
      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
        <thead>
          <tr style={{ background: "#1e3a5f", color: "#000" }}>
            {columns.map(col => (
              <th key={col.key} style={{ border: "1px solid #555", padding: "2px 3px", textAlign: "center", fontSize: 12, whiteSpace: "nowrap" }}>
                {col.col}
              </th>
            ))}
          </tr>
          <tr style={{ background: "#2d4f7c", color: "#000" }}>
            {columns.map(col => (
              <th key={col.key} style={{ border: "1px solid #555", padding: "3px", textAlign: "center", fontSize: 12, lineHeight: 1.3, minWidth: col.key === "serialNo" ? 24 : 72 }}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id || i} style={{ background: i % 2 === 0 ? "#000" : "#000" }}>
              {columns.map(col => (
                <td key={col.key} style={{ border: "1px solid #ccc", padding: "2px 3px", textAlign: "center", fontSize: 11, verticalAlign: "middle" }}>
                  {dateFieldSet.includes(col.key) ? fmtDate(row[col.key]) : fmt(row[col.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div>
      {/* ── Page Header ── */}
      <div className="bg-white border-b-4 px-6 py-2 mb-2 text-center shadow-sm rounded-t-lg">
        <h1 className="text-2xl font-black uppercase tracking-tight">Form No. MBP - 4</h1>
        <h2 className="text-lg font-bold mt-1">Register of Contracts or Arrangements in which Directors are Interested</h2>
        <p className="text-[10px] italic text-slate-500 mt-1 max-w-4xl mx-auto">
          [Pursuant to section 189 of the Companies Act, 2013 and rule 16 of the
          Companies (Meetings of Board and its Powers) Rules 2014]
        </p>
      </div>

      {/* ── Controls ── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 mb-2 flex flex-wrap gap-3 items-end justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => { setShowModal(true); setStatus(null); }}
            disabled={!accountId}
            className="bg-primary text-white text-xs px-4 py-2 rounded-md font-bold uppercase shadow-sm hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
          >
            + Add New Entry
          </button>
          <button
            onClick={fetchData}
            className="bg-primary text-white text-xs px-4 py-2 rounded-md font-bold uppercase shadow-sm hover:bg-blue-700 transition"
          >
            🔄 Reload
          </button>
          <button
            onClick={handleDownloadPdf}
            disabled={!hasData || generatingPdf}
            className="bg-green-600 text-white text-xs px-4 py-2 rounded-md font-bold uppercase shadow-sm hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
          >
            ⬇ Download PDF
          </button>
        </div>
      </div>

      {/* ── Status Banner ── */}
      {status && (
        <div className={`mb-2 px-4 py-3 rounded-lg text-sm font-medium border ${
          status.type === "success"
            ? "bg-green-50 text-green-800 border-green-200"
            : "bg-red-50 text-red-800 border-red-200"
        }`}>
          {status.message}
        </div>
      )}

      {/* ── Screen: per-record groups ── */}
      {loading ? (
        <div className="text-center py-10 text-gray-500 text-sm">Loading...</div>
      ) : error ? (
        <div className="text-center py-10 text-red-500 text-sm">{error}</div>
      ) : !hasData ? (
        <div className="text-center py-10 text-gray-400 text-sm italic">
          {accountId ? "No entries found for this company." : "Select a company to view records."}
        </div>
      ) : (
        <>
          {parentRecords.map((parent) => {
            const contracts = parent.contractEntries        || [];
            const interests = parent.directorInterestEntries || [];
            if (contracts.length === 0 && interests.length === 0) return null;

            const contractRows = withSerial(contracts);
            const interestRows = withSerial(interests);

            return (
              <div key={parent.id} className="mb-10">

                {/* ── Record info bar — shown ONCE ── */}
                <div className="bg-slate-800 text-white px-4 py-2 rounded-t-xl flex flex-wrap gap-x-6 gap-y-1 text-xs">
                  <span><span className="text-slate-400 mr-1">Place:</span><b>{fmt(parent.place)}</b></span>
                  <span><span className="text-slate-400 mr-1">Date:</span><b>{fmtDate(parent.date)}</b></span>
                  <span><span className="text-slate-400 mr-1">Signed By:</span><b>{fmt(parent.signedBy)}</b></span>
                </div>

                {/* ── Section A: Contract Entries ── */}
                {contracts.length > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center gap-2 mb-2 px-1">
                      <span className="bg-blue-600 text-white text-[10px] font-bold px-3 py-0.5 rounded-full uppercase tracking-wide">
                        Section A — Contract Entries
                      </span>
                      <span className="text-xs text-gray-500">{contracts.length} record{contracts.length > 1 ? "s" : ""}</span>
                    </div>

                    {/* Contract Part 1: (1)–(9) */}
                    <SubTable
                      columns={CONTRACT_PART_ONE}
                      rows={contractRows}
                      partLabel="Part 1 — Columns (1) to (9)"
                      dateFieldSet={contractDateFields}
                    />

                    {/* Contract Part 2: S.No + (10)–(17) */}
                    <div className="mt-3">
                      <SubTable
                        columns={CONTRACT_PART_TWO}
                        rows={contractRows}
                        partLabel="Part 2 — Columns (10) to (17)"
                        dateFieldSet={contractDateFields}
                      />
                    </div>
                  </div>
                )}

                {/* ── Section B: Director Interest Entries ── */}
                {interests.length > 0 && (
                  <div className="mt-5">
                    <div className="flex items-center gap-2 mb-2 px-1">
                      <span className="bg-amber-600 text-white text-[10px] font-bold px-3 py-0.5 rounded-full uppercase tracking-wide">
                        Section B — Director Interest Entries
                      </span>
                      <span className="text-xs text-gray-500">{interests.length} record{interests.length > 1 ? "s" : ""}</span>
                    </div>

                    <SubTable
                      columns={INTEREST_PART_ONE}
                      rows={interestRows}
                      partLabel="Director Interest — All Columns"
                      dateFieldSet={interestDateFields}
                    />
                  </div>
                )}

                <div className="text-right text-xs text-gray-500 mt-2 pr-1">
                  Contract entries: <span className="font-bold text-gray-700">{contracts.length}</span>
                  &nbsp;|&nbsp;
                  Interest entries: <span className="font-bold text-gray-700">{interests.length}</span>
                </div>
              </div>
            );
          })}

          <div className="text-right text-xs text-gray-500 mb-4 pr-1">
            Total contracts: <span className="font-bold text-gray-700">{totalContracts}</span>
            &nbsp;|&nbsp;
            Total interests: <span className="font-bold text-gray-700">{totalInterests}</span>
          </div>
        </>
      )}

      {/* ── Hidden PDF Print Area ── */}
      {hasData && (
        <div id="mbp4-pdf-area" style={{ display: "none" }}>
          <style>{`@media print { #mbp4-pdf-area { display: block !important; } }`}</style>

          {/* Global PDF title */}
          <div style={{ textAlign: "center", marginBottom: 10, fontFamily: "Arial, sans-serif" }}>
            <h1 style={{ fontSize: 15, fontWeight: "bold", margin: 0 }}>Form No. MBP - 4</h1>
            <h2 style={{ fontSize: 13, margin: "2px 0" }}>Register of Contracts or Arrangements in which Directors are Interested</h2>
            <p style={{ fontSize: 10, margin: 0, color: "#555", fontStyle: "italic" }}>
              [Pursuant to section 189 of the Companies Act, 2013 and rule 16 of the
              Companies (Meetings of Board and its Powers) Rules 2014]
            </p>
          </div>

          {parentRecords.map((parent, idx) => {
            const contracts    = parent.contractEntries        || [];
            const interests    = parent.directorInterestEntries || [];
            if (contracts.length === 0 && interests.length === 0) return null;

            const contractRows = withSerial(contracts);
            const interestRows = withSerial(interests);

            return (
              <div key={parent.id} style={{ marginBottom: 22, fontFamily: "Arial, sans-serif" }}>

                {/* Record info — ONCE per record */}
                <div style={{
                  background: "#1e3a5f", color: "#000",
                  padding: "5px 8px", marginBottom: 8,
                  fontSize: 12, borderRadius: 3,
                  display: "flex", flexWrap: "wrap", gap: "14px",
                }}>
                  <span><span style={{ color: "#000" }}>Place: </span><b>{fmt(parent.place)}</b></span>
                  <span><span style={{ color: "#000" }}>Date: </span><b>{fmtDate(parent.date)}</b></span>
                  <span><span style={{ color: "#000" }}>Signed By: </span><b>{fmt(parent.signedBy)}</b></span>
                </div>

                {/* Section A: Contract Entries */}
                {contracts.length > 0 && (
                  <>
                    <div style={{ fontSize: 10, fontWeight: "bold", color: "#1d4ed8", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.8 }}>
                      Section A — Contract Entries
                    </div>
                    <PdfSubTable columns={CONTRACT_PART_ONE} rows={contractRows} partLabel="Part 1 — Columns (1) to (9)"  dateFieldSet={contractDateFields} />
                    <PdfSubTable columns={CONTRACT_PART_TWO} rows={contractRows} partLabel="Part 2 — Columns (10) to (17)" dateFieldSet={contractDateFields} />
                  </>
                )}

                {/* Section B: Director Interest Entries */}
                {interests.length > 0 && (
                  <>
                    <div style={{ fontSize: 10, fontWeight: "bold", color: "#b45309", marginTop: 10, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.8 }}>
                      Section B — Director Interest Entries
                    </div>
                    <PdfSubTable columns={INTEREST_PART_ONE} rows={interestRows}  dateFieldSet={interestDateFields} />
                  </>
                )}

                {/* Divider between records */}
                {idx < parentRecords.length - 1 && (
                  <div style={{ borderTop: "1.5px dashed #94a3b8", marginTop: 14 }} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Add Entry Modal ── */}
      {showModal && (
        <AddEntryModal
          onClose={() => setShowModal(false)}
          onSave={handleSaveEntry}
          saving={saving}
          parentRecords={parentRecords}
        />
      )}
    </div>
  );
};

export default Mbp4Viewer;